import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Capacitor } from '@capacitor/core'
import { ArrowLeft, CreditCard } from '@/components/icons'
import { PlanSelection } from '@/components/subscription/PlanSelection'
import { CompanyDataCard } from '@/components/subscription/CompanyDataCard'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackButton } from '@/components/topbar/TopBarBackButton'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ListSkeleton } from '@/components/ui/skeleton-presets'
import { EmptyState } from '@/components/EmptyState'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'
import { useAuth } from '@/hooks/useAuth'
import {
  useSubscriptionManagementUrl,
  useSubscriptionPurchases,
  useSubscriptionStatus,
} from '@/lib/hooks/subscription'

const OPS_WEB_URL = import.meta.env.VITE_OPS_WEB_URL as string | undefined

export const Route = createFileRoute('/_auth/operations/subscriptions/')({
  component: AdminSubscriptionsRoute,
})

function formatDate(value: string | number | null | undefined): string | null {
  if (!value) return null
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

function formatStore(store: string): string {
  switch (store) {
    case 'app_store':
      return 'App Store'
    case 'play_store':
      return 'Play Store'
    case 'stripe':
      return 'Stripe'
    default:
      return store
  }
}

function AdminSubscriptionsRoute() {
  const statusQuery = useSubscriptionStatus()
  const purchasesQuery = useSubscriptionPurchases()
  const currentCrewQuery = useCurrentCrewMember()
  const isDesktop = useIsDesktop()
  const navigate = useNavigate()
  const { signOut } = useAuth()

  // On the web console, expired owners pick a plan inline and check out via
  // RevenueCat Web Billing. Native iOS can't run that checkout (external
  // purchase rules), so it links out to the ops web console instead — the
  // subscription gate lands the owner right back on this page after sign-in.
  const isNative = Capacitor.isNativePlatform()

  const isLoading = statusQuery.isLoading || purchasesQuery.isLoading
  const status = statusQuery.data
  const purchases = purchasesQuery.data?.purchases ?? []

  const hasActiveSubscription = !!status?.hasSubscription
  const isOwner = !!currentCrewQuery.data?.isOwner

  // RevenueCat customer portal (Web Billing only): change plan, update card,
  // cancel, invoices. Only owners with a live web subscription get a URL.
  const managementUrlQuery = useSubscriptionManagementUrl({
    enabled: !isLoading && isOwner && hasActiveSubscription,
  })
  const managementUrl = isOwner ? (managementUrlQuery.data?.url ?? null) : null
  const isManagementUrlLoading = isOwner && managementUrlQuery.isLoading

  const openManagementPortal = () => {
    if (!managementUrl) return
    // Open synchronously from the tap so Safari doesn't treat it as a popup;
    // the URL is single-use, so refetch immediately for the next tap.
    window.open(managementUrl, '_blank')
    void managementUrlQuery.refetch()
  }

  const body = (
    <>
      {isLoading ? (
        <ListSkeleton />
      ) : (
        <div className="flex flex-col gap-4">
          {hasActiveSubscription ? (
            <Card className="flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-content-primary-subtle tracking-[0.07px]">
                    Status
                  </p>
                  <p className="text-sm font-medium text-content-primary-bold tracking-[0.07px]">
                    Active
                  </p>
                </div>
                {status?.expiresDate && (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-content-primary-subtle tracking-[0.07px]">
                      Renews / Expires
                    </p>
                    <p className="text-sm font-medium text-content-primary-bold tracking-[0.07px]">
                      {formatDate(status.expiresDate)}
                    </p>
                  </div>
                )}
              </div>
              {isOwner && (managementUrl || isManagementUrlLoading) && (
                <div className="flex flex-col gap-2">
                  <Button
                    variant="primary"
                    className="bg-background-brand-bold"
                    disabled={!managementUrl || managementUrlQuery.isFetching}
                    onClick={openManagementPortal}
                  >
                    Manage subscription
                  </Button>
                  <p className="text-xs text-content-primary-subtle tracking-[0.07px]">
                    Change plan, update payment method, or cancel. Opens in your
                    browser.
                  </p>
                </div>
              )}
            </Card>
          ) : isOwner && !isNative ? (
            <Card className="flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <p className="text-base font-semibold text-content-primary-bold tracking-[0.07px]">
                  Subscription expired
                </p>
                <p className="text-sm text-content-primary-subtle tracking-[0.07px]">
                  Your subscription has expired. Choose a plan to keep your
                  account active.
                </p>
              </div>
              <PlanSelection />
            </Card>
          ) : (
            <Card className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <p className="text-base font-semibold text-content-primary-bold tracking-[0.07px]">
                  Subscription expired
                </p>
                <p className="text-sm text-content-primary-subtle tracking-[0.07px]">
                  {isOwner
                    ? 'In-app subscriptions on the App Store are no longer offered. Continue your Pronteer subscription on the web to keep your account active.'
                    : 'Your company’s Pronteer subscription has expired. Please contact the company owner to renew.'}
                </p>
              </div>
              {isOwner && (
                <Button
                  variant="primary"
                  className="bg-background-brand-bold"
                  disabled={!OPS_WEB_URL}
                  onClick={() => {
                    if (OPS_WEB_URL) window.open(OPS_WEB_URL, '_blank')
                  }}
                >
                  Continue on the Web
                </Button>
              )}
              <Button
                variant="outline"
                onClick={async () => {
                  await signOut()
                  navigate({ to: '/' })
                }}
              >
                Sign Out
              </Button>
            </Card>
          )}

          {isOwner && <CompanyDataCard />}

          {purchases.length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title="No purchases yet"
              description="Subscription purchases will appear here once a plan is started."
            />
          ) : (
            <Card className="flex flex-col gap-3">
              <p className="text-sm font-medium text-content-primary-bold tracking-[0.07px]">
                Purchase history
              </p>
              <div className="flex flex-col gap-3">
                {purchases.map((p, idx) => (
                  <div key={p.id} className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-content-primary-bold tracking-[0.07px]">
                        {p.productName ?? p.productId}
                      </p>
                      <p className="text-xs text-content-primary-subtle tracking-[0.07px]">
                        {formatStatus(p.status)}
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-content-primary-subtle tracking-[0.07px]">
                        {formatStore(p.store)}
                        {p.country ? ` · ${p.country}` : ''}
                      </p>
                      {p.currentPeriodEndsAt && (
                        <p className="text-xs text-content-primary-subtle tracking-[0.07px]">
                          Until {formatDate(p.currentPeriodEndsAt)}
                        </p>
                      )}
                    </div>
                    {p.entitlements.length > 0 && (
                      <p className="text-xs text-content-primary-subtle tracking-[0.07px]">
                        {p.entitlements.join(', ')}
                      </p>
                    )}
                    {idx < purchases.length - 1 && (
                      <div className="h-px bg-border mt-2" />
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </>
  )

  const breadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => navigate({ to: '/operations/settings' })}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to settings"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        <Link to="/operations" className="hover:underline">
          Operations
        </Link>
        <span className="text-content-primary-subtle"> / </span>
        <Link to="/operations/settings" className="hover:underline">
          Settings
        </Link>
        <span className="text-content-primary-subtle"> / Subscription Status</span>
      </span>
    </span>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader title="Subscription Status" breadcrumb={breadcrumb} />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={<TopBarBackButton title="Subscription Status" />}
      className="pt-6"
    >
      {body}
    </AppLayout>
  )
}
